/**
 * CertificateCard — a single completion certificate on `/learn/certificates`.
 *
 * Presentational only (motion/stagger is applied by the page). Shows the source
 * (Course / Program), recipient, issued date, and a download action linking to
 * the short-lived signed URL. When the URL is missing, a disabled "Preparing…"
 * state is shown — the endpoint self-heals so a later refetch fills it in.
 */

import React, { useState } from 'react';
import { format } from 'date-fns';
import { ArrowDownTrayIcon, EyeIcon } from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import type { IssuedCertificate } from '../../types/learnerCertificates';

interface CertificateCardProps {
  certificate: IssuedCertificate;
}

function formatIssued(issuedAt: string): string | null {
  const date = new Date(issuedAt);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'd MMM yyyy');
}

const CertificateCard: React.FC<CertificateCardProps> = ({ certificate }) => {
  const { source_type, title, recipient_name, issued_at, download_url } = certificate;
  const [viewerOpen, setViewerOpen] = useState(false);
  const issued = formatIssued(issued_at);
  const heading = title ?? 'Completion certificate';
  const isProgram = source_type === 'program';

  return (
    <div className="group flex flex-col gap-4 rounded-2xl border border-border-primary/60 bg-surface-elevated p-5 transition-shadow duration-200 hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
            />
          </svg>
        </div>
        <span className={isProgram ? 'badge-secondary' : 'badge-primary'}>
          {isProgram ? 'Program' : 'Course'}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold leading-snug text-text-primary">{heading}</h2>
        {recipient_name ? (
          <p className="text-sm text-text-secondary">Issued to {recipient_name}</p>
        ) : null}
        {issued ? <p className="text-xs text-text-secondary">Issued {issued}</p> : null}
      </div>

      {download_url ? (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setViewerOpen(true)}
            className="focus-ring inline-flex items-center justify-center gap-2 rounded-full bg-primary-600 dark:bg-primary-350 px-4 py-2 text-sm font-medium transition-[transform,background-color] duration-150 ease-out hover:bg-primary-700 dark:hover:bg-primary-400 active:scale-[0.97]"
          >
            <EyeIcon className="h-4 w-4" aria-hidden="true" />
            Show
          </button>
          <a
            href={download_url}
            target="_blank"
            rel="noopener noreferrer"
            className="focus-ring inline-flex items-center justify-center gap-2 rounded-full border border-primary-600/40 px-4 py-2 text-sm font-medium text-primary-600 transition-[transform,background-color] duration-150 ease-out hover:bg-primary-500/10 active:scale-[0.97] dark:border-primary-400/40 dark:text-primary-300"
          >
            <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
            Download
          </a>

          <Modal
            isOpen={viewerOpen}
            onClose={() => setViewerOpen(false)}
            title={heading}
            size="4xl"
            data-testid="certificate-viewer"
          >
            {/* A4-landscape aspect so the frame hugs the certificate — no dead
                vertical space. #view=Fit makes the browser viewer fill the
                frame; the fragment never reaches S3, so the signature holds. */}
            <iframe
              src={`${download_url}#view=Fit&toolbar=0&navpanes=0`}
              title={`Certificate — ${heading}`}
              className="aspect-[297/210] w-full rounded-lg border border-border-primary/40 bg-white"
            />
          </Modal>
        </div>
      ) : (
        <span
          className="mt-1 inline-flex cursor-not-allowed items-center justify-center gap-2 self-start rounded-lg border border-border-primary/60 px-4 py-2 text-sm font-medium text-text-secondary"
          aria-disabled="true"
          title="Your certificate is being prepared — check back shortly."
        >
          <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
          Preparing…
        </span>
      )}
    </div>
  );
};

export default CertificateCard;
